import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Zap, LayoutTemplate, ArrowRight, BarChart3, Activity, MessageSquare, Layers } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function Proposal() {
  return <div className="min-h-screen bg-white text-[#1A1A1A] font-sans selection:bg-[#E2001A] selection:text-white" data-id="element-569">
      {/* Top Accent Line */}
      <div className="fixed top-0 left-0 w-full h-1.5 bg-[#E2001A] z-50" data-id="element-570" />

      {/* 1. Hero Section */}
      <section className="min-h-[90vh] flex flex-col justify-center items-center px-6 relative" data-id="element-571">
        <motion.div className="max-w-4xl mx-auto text-center" initial="hidden" animate="visible" variants={staggerContainer} data-id="element-572">
          <motion.div variants={fadeUp} className="mb-8" data-id="element-573">
            <span className="inline-block py-1 px-3 border border-[#E2001A]/20 bg-[#E2001A]/5 text-[#E2001A] text-xs font-bold tracking-widest uppercase rounded-full" data-id="element-574">
              A Proposal for Bayer
            </span>
          </motion.div>
          <motion.h1 variants={fadeUp} className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight leading-[1.1] mb-8 text-[#1A1A1A]" data-id="element-575">
            From Slides to <br className="hidden md:block" data-id="element-576" />
            <span className="text-[#E2001A]" data-id="element-577">Strategy.</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="text-xl md:text-2xl text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-578">
            Rethinking field team engagement through interactive, data-driven
            workshop experiences.
          </motion.p>
        </motion.div>
      </section>

      {/* 2. The Problem */}
      <section className="py-32 px-6 bg-gray-50" data-id="element-579">
        <motion.div className="max-w-5xl mx-auto" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} data-id="element-580">
          <motion.div variants={fadeUp} className="mb-16 md:mb-24" data-id="element-581">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-2xl" data-id="element-582">
              Traditional workshops are broken.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-12 md:gap-8" data-id="element-583">
            {[{
            title: 'Passive Audiences',
            desc: 'Hours of one-way presentations leave field teams disengaged and uninspired.',
            icon: Users
          }, {
            title: 'Generic Content',
            desc: 'One-size-fits-all messaging fails to address the nuanced realities of different specialties.',
            icon: Layers
          }, {
            title: 'No Actionable Output',
            desc: 'Participants leave with a binder of slides, not a concrete strategy they can use tomorrow.',
            icon: LayoutTemplate
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="relative" data-id="element-584">
                <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center mb-6 text-[#1A1A1A]" data-id="element-585">
                  <item.icon size={24} data-id="element-586" />
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-587">{item.title}</h3>
                <p className="text-gray-500 leading-relaxed" data-id="element-588">{item.desc}</p>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* 3. The Format */}
      <section className="py-32 px-6" data-id="element-589">
        <motion.div className="max-w-5xl mx-auto" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} data-id="element-590">
          <motion.div variants={fadeUp} className="text-center mb-20" data-id="element-591">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6" data-id="element-592">
              The New Format
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto" data-id="element-593">
              A structured, digital-first journey that turns passive listeners
              into active strategists.
            </p>
          </motion.div>

          <div className="relative" data-id="element-594">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-8 left-0 w-full h-0.5 bg-gray-100" data-id="element-595" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-6 relative z-10" data-id="element-596">
              {[{
              step: '01',
              title: 'Icebreaker',
              desc: 'Challenge assumptions with a quick, provocative group quiz.'
            }, {
              step: '02',
              title: 'Deep Dive',
              desc: 'Unpack the specific beliefs and tensions of assigned specialties.'
            }, {
              step: '03',
              title: 'Strategy',
              desc: 'Rank drivers, predict behaviors, and select the best arguments.'
            }, {
              step: '04',
              title: 'Share-Back',
              desc: 'Present a synthesized strategy card to the wider room.'
            }].map((phase, i) => <motion.div key={i} variants={fadeUp} className="relative" data-id="element-597">
                  <div className="w-16 h-16 rounded-2xl bg-white border-2 border-gray-100 flex items-center justify-center mb-6 shadow-sm mx-auto md:mx-0" data-id="element-598">
                    <span className="text-lg font-bold text-[#E2001A]" data-id="element-599">
                      {phase.step}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-center md:text-left" data-id="element-600">
                    {phase.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed text-center md:text-left" data-id="element-601">
                    {phase.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </motion.div>
      </section>

      {/* 3b. The Storyboard */}
      <section className="py-32 px-6 bg-white" data-id="element-602">
        <motion.div className="max-w-5xl mx-auto" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} data-id="element-603">
          <motion.div variants={fadeUp} className="text-center mb-16" data-id="element-604">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6" data-id="element-605">
              The Full Storyboard
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto" data-id="element-606">
              13 steps across 4 phases — a complete journey from icebreaker to
              actionable strategy.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="relative" data-id="element-607">
            <div className="bg-gray-50 rounded-3xl p-4 md:p-8 border border-gray-100 shadow-lg" data-id="element-608">
              <img src="https://cdn.magicpatterns.com/uploads/4Fivas6AAiZgRiajfCDHxr/Screenshot_2026-04-14_at_09.07.11.png" alt="Workshop storyboard showing 13 steps across 4 phases: Icebreaker, Deep Dive, Strategy, and Share Back" className="w-full h-auto rounded-2xl" data-id="element-609" />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* 4. What Makes It Different */}
      <section className="py-32 px-6 bg-[#1A1A1A] text-white" data-id="element-610">
        <motion.div className="max-w-5xl mx-auto" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} data-id="element-611">
          <motion.div variants={fadeUp} className="mb-20" data-id="element-612">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-id="element-613">
              Why it works.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-614">
            {[{
            title: 'Interactive, not passive.',
            desc: 'Groups vote, rank, and predict outcomes in real-time. The UI responds instantly with feedback and insights.',
            icon: Activity
          }, {
            title: 'Data-driven reality checks.',
            desc: 'Teams test their gut instincts against actual specialty data, revealing critical gaps in their approach.',
            icon: BarChart3
          }, {
            title: 'Collaborative problem solving.',
            desc: 'Designed for table-groups to debate and align before locking in their answers.',
            icon: MessageSquare
          }, {
            title: 'Actionable takeaways.',
            desc: "Every group builds a concrete 'Strategy Card' detailing segmentation, features, and motivation.",
            icon: Target
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="bg-[#2A2A2A] p-8 md:p-10 rounded-3xl border border-white/5" data-id="element-615">
                <item.icon className="text-[#E2001A] mb-6" size={32} data-id="element-616" />
                <h3 className="text-2xl font-bold mb-4" data-id="element-617">{item.title}</h3>
                <p className="text-gray-400 text-lg leading-relaxed" data-id="element-618">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* 5. A Glimpse (Styled Mockups) */}
      <section className="py-32 px-6 bg-gray-50 overflow-hidden" data-id="element-619">
        <motion.div className="max-w-6xl mx-auto" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} data-id="element-620">
          <motion.div variants={fadeUp} className="text-center mb-20" data-id="element-621">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6" data-id="element-622">
              A Glimpse Inside
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto" data-id="element-623">
              Clean, intuitive interfaces designed to keep the focus on the
              conversation, not the software.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-624">
            {/* Screenshot 1: Welcome / Hook */}
            <motion.div variants={fadeUp} className="relative group" data-id="element-625">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform -rotate-1 group-hover:rotate-0 transition-transform duration-500" data-id="element-626">
                <img src="https://cdn.magicpatterns.com/uploads/ohXTFbjkZ9RMyjp8w2ANBy/Screenshot_2026-04-14_at_09.03.24.png" alt="Workshop welcome screen showing the opening challenge and objectives" className="w-full h-auto" data-id="element-627" />
              </div>
              <div className="mt-5 text-center" data-id="element-628">
                <p className="text-sm font-bold text-[#E2001A]" data-id="element-629">The Hook</p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-630">
                  A provocative opening question that sets the stage.
                </p>
              </div>
            </motion.div>

            {/* Screenshot 2: Feature Selection */}
            <motion.div variants={fadeUp} className="relative group md:mt-8" data-id="element-631">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform rotate-1 group-hover:rotate-0 transition-transform duration-500" data-id="element-632">
                <img src="https://cdn.magicpatterns.com/uploads/a7RvnLGMfNHXSFamybxjo3/Screenshot_2026-04-14_at_09.03.56.png" alt="Interactive feature selection exercise where teams pick their top arguments" className="w-full h-auto" data-id="element-633" />
              </div>
              <div className="mt-5 text-center" data-id="element-634">
                <p className="text-sm font-bold text-[#E2001A]" data-id="element-635">The Exercise</p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-636">
                  Teams select and debate before the data reveals the answer.
                </p>
              </div>
            </motion.div>

            {/* Screenshot 3: Data Reveal */}
            <motion.div variants={fadeUp} className="relative group" data-id="element-637">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform -rotate-1 group-hover:rotate-0 transition-transform duration-500" data-id="element-638">
                <img src="https://cdn.magicpatterns.com/uploads/wKe2hkHAjSUipLPSV83t6R/Screenshot_2026-04-14_at_09.04.42.png" alt="Data reveal showing observed feature effectiveness ranking with scores" className="w-full h-auto" data-id="element-639" />
              </div>
              <div className="mt-5 text-center" data-id="element-640">
                <p className="text-sm font-bold text-[#E2001A]" data-id="element-641">The Reveal</p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-642">
                  Real data challenges assumptions and sharpens strategy.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 6. The Ask / CTA */}
      <section className="py-32 px-6 bg-white text-center" data-id="element-643">
        <motion.div className="max-w-3xl mx-auto" initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} data-id="element-644">
          <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-black tracking-tight mb-8" data-id="element-645">
            Let's build this together.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-xl text-gray-500 mb-12 leading-relaxed" data-id="element-646">
            Whether for Nephrology, Cardiology, or a completely new therapeutic
            area, this format adapts to your strategic goals.
          </motion.p>
          <motion.button variants={fadeUp} className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white transition-all duration-200 bg-[#E2001A] rounded-full hover:bg-[#C60017] shadow-lg hover:shadow-xl" data-id="element-647">
            Discuss a Pilot
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-648" />
          </motion.button>
        </motion.div>
      </section>
    </div>;
}